import { IntlShape } from 'react-intl';
import { translate } from '../utility/messageTranslator/translate';
import { MARS4_COEF } from '../config/constants';

export type Notification = {
  id: number;
  type: NotificationType;
  createdAt: number;
} & NotificationItem;

type NotificationItem =
  | NotificationJobFinished
  | NotificationBuildingCompleted
  | NotificationMarketplaceItemSold
  | NotificationProductMinted
  | NotificationLandMinted
  | NotificationResource
  | NotificationGift
  | NotificationVoting
  | NotificationRenting
  | NotificationWallet
  | NotificationMessage
  | NotificationCrafting;

type BasicContentJson = {
  label?: string;
  labelColor?: string;
};

type NotificationJobFinished = {
  type: 'job_finished';
  contentJson: BasicContentJson & {
    vehicleId: number;
    landSlot: number;
    landNumber: number;
    task: 'exploration' | 'mining';
  };
};

type NotificationBuildingCompleted = {
  type: 'building_completed';
  contentJson: BasicContentJson & {
    blueprint: string;
    landSlot: number;
    landNumber: number;
  };
};

type NotificationMarketplaceItemSold = {
  type: 'marketplace_item_sold';
  contentJson: BasicContentJson & {
    itemType: 'resource' | string;
    itemId: string;
    amount: number;
    pricePerKg: number;
  };
};

type NotificationProductMinted = {
  type: 'product_minted';
  contentJson: BasicContentJson & {
    tokenId: number;
  };
};

type NotificationLandMinted = {
  type: 'land_minted';
  contentJson: BasicContentJson & {
    landNumber: number;
  };
};

type NotificationResource = {
  type: 'resource';
  contentJson: BasicContentJson & {
    resource: string;
    amount: number;
  };
};

type NotificationGift = {
  type: 'gift';
  contentJson: BasicContentJson & {
    balance: string;
    amount: number;
  };
};

type NotificationVoting = {
  type: 'voting';
  contentJson: BasicContentJson & {
    title: string;
  };
};

type NotificationRenting = {
  type: 'renting';
  contentJson: BasicContentJson & {
    tokenId: number;
    task: 'exploration' | 'mining';
  };
};

type NotificationWallet = {
  type: 'wallet';
  contentJson: BasicContentJson & {};
};

type NotificationMessage = {
  type: 'message';
  contentJson: BasicContentJson & { message: string };
};

type NotificationCrafting = {
  type: 'crafting';
  contentJson: BasicContentJson & {
    nftId: number;
    recipeId: number;
    recipeName: string;
  };
};

export interface NotificationEvents {
  newNotification: (notification: Notification) => void;
  refresh: () => void;
}

export type NotificationType =
  | 'job_finished'
  | 'building_completed'
  | 'marketplace_item_sold'
  | 'product_minted'
  | 'land_minted'
  | 'resource'
  | 'gift'
  | 'voting'
  | 'renting'
  | 'wallet'
  | 'message'
  | 'crafting';

export const getNotificationTitle = (intl: IntlShape, notification: Notification): string => {
  switch (notification.type) {
    case 'job_finished':
      return translate(intl, 'NOTIFICATION.TITLE_JOB_FINISHED', 'Job finished');
    case 'building_completed':
      return translate(intl, 'NOTIFICATION.TITLE_BUILDING_COMPLETED', 'Building completed');
    case 'marketplace_item_sold':
      return translate(intl, 'NOTIFICATION.TITLE_MARKETPLACE_ITEM_SOLD', 'Item sold');
    case 'product_minted':
      return translate(intl, 'NOTIFICATION.TITLE_PRODUCT_MINTED', 'Product item acquired');
    case 'land_minted':
      return translate(intl, 'NOTIFICATION.TITLE_LAND_MINTED', 'Lan plot acquired');
    case 'resource':
      return translate(intl, 'NOTIFICATION.TITLE_RESOURCE', 'Resource acquired');
    case 'gift':
      return translate(intl, 'NOTIFICATION.TITLE_GIFT', 'Gift acquired');
    case 'voting':
      return translate(intl, 'NOTIFICATION.TITLE_VOTING', 'Voting announced');
    case 'renting':
      return translate(intl, 'NOTIFICATION.TITLE_RENTING', 'NFT rented');
    case 'wallet':
      return translate(intl, 'NOTIFICATION.TITLE_WALLET', 'Balance changed');
    case 'message':
      return translate(intl, 'NOTIFICATION.TITLE_MESSAGE', 'New message');
    case 'crafting':
      return translate(intl, 'NOTIFICATION.TITLE_CRAFTING', 'Crafting completed');
  }
};

export const getNotificationBody = (intl: IntlShape, notification: Notification): string | null => {
  switch (notification.type) {
    case 'job_finished':
      return translate(intl, 'NOTIFICATION.BODY_JOB_FINISHED', `:task finished on land :landNumber!`)
        .replace(':task', notification.contentJson.task === 'mining' ? 'Mining' : 'Exploration')
        .replace(':landNumber', `#${notification.contentJson.landNumber} (Slot#${notification.contentJson.landSlot})`);
    case 'building_completed':
      return translate(
        intl,
        'NOTIFICATION.BODY_BUILDING_COMPLETED',
        `Construction of ':name' finished on land :landNumber!`,
      )
        .replace(':name', notification.contentJson.blueprint)
        .replace(':landNumber', `#${notification.contentJson.landNumber} (Slot#${notification.contentJson.landSlot})`);
    case 'marketplace_item_sold':
      return translate(
        intl,
        'NOTIFICATION.BODY_MARKETPLACE_ITEM_SOLD',
        `You have sold :quantity of :name for :price $MARS4`,
      )
        .replace(':quantity', notification.contentJson.amount.toLocaleString())
        .replace(
          ':name',
          notification.contentJson.itemType === 'resource'
            ? notification.contentJson.itemId.replace('_', ' ').toUpperCase()
            : '-',
        )
        .replace(':price', (notification.contentJson.amount * notification.contentJson.pricePerKg).toLocaleString());
    case 'product_minted':
      return translate(intl, 'NOTIFICATION.BODY_PRODUCT_MINTED', `Check for new NFT #:token`).replace(
        ':token',
        notification.contentJson.tokenId.toString(),
      );
    case 'land_minted':
      return translate(intl, 'NOTIFICATION.BODY_LAND_MINTED', `Check for new Land #:land`).replace(
        ':land',
        notification.contentJson.landNumber.toString(),
      );
    case 'resource':
      return translate(intl, 'NOTIFICATION.BODY_RESOURCE', 'You received :amount of :resource')
        .replace(':amount', notification.contentJson.amount.toString())
        .replace(':resource', notification.contentJson.resource.replace('_', ' ').toUpperCase());
    case 'gift':
      const amount = notification.contentJson.amount;
      const amountString = notification.contentJson.balance.includes('mars4')
        ? `${amount / MARS4_COEF} $MARS4`
        : notification.contentJson.balance.includes('shoot')
        ? `${amount / MARS4_COEF} $SHOOT`
        : notification.contentJson.balance.includes('blueprint')
        ? `${amount} Blueprint`
        : `${amount} of ${notification.contentJson.balance.replace('_', ' ').toUpperCase()}`;
      return translate(intl, 'NOTIFICATION.BODY_RESOURCE', 'You received :amount').replace(':amount', amountString);
    case 'voting':
      return translate(intl, 'NOTIFICATION.BODY_VOTING', ':title voting announced!').replace(
        ':title',
        notification.contentJson.title,
      );
    case 'renting':
      return translate(intl, 'NOTIFICATION.BODY_RENTING', 'Your NFT #:token rented for :task!')
        .replace(':token', notification.contentJson.tokenId.toString())
        .replace(':task', notification.contentJson.task === 'mining' ? 'mining' : 'exploration');
    case 'wallet':
      return null;
    case 'message':
      const message = notification.contentJson.message;
      return message.length > 30 ? message.slice(0, 30) + '...' : message;
    case 'crafting':
      return translate(intl, 'NOTIFICATION.BODY_CRAFTING_COMPLETED', `Crafting of ':name' recipe finished!`).replace(
        ':name',
        notification.contentJson.recipeName,
      );
  }
};
